import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import {
  COMMODITY_NAME_COTTON,
  PRACTICE_NAME_IRRIGATED,
  PRACTICE_NAME_NON_IRRIGATED,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { useCardController } from "@datanac/datanac-mui-toolkit/dist/hooks";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  Icon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useArcCoCountyYieldOverrides } from "api-toolkit/useArcCoCountyYieldOverrides";
import { useArcPlc } from "api-toolkit/useArcPlc";
import { _sessionManager, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import {
  ClearFieldAdornment,
  isClearedFieldValue,
  MASK_NUMERIC,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";
import { ArcPlcIcon, InfoIcon } from "components/Icons/Icons";
import { MODULE_ARC_PLC } from "components/Menu/config/constants";
import { useWeatherReport } from "components/Weather/useWeatherReport";
import { YieldForecast } from "components/Weather/YieldForecast";
import { WORKSPACE_VIEW_CARD } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatNumber, formatPrice } from "utility/formatters";
import { v4 as uuidv4, validate as validateUUID } from "uuid";

import {
  calculateCombinedArcCoResults,
  getShareWeightedHistoricalIrrigationPercentage,
  normalizeArcPlcRouteTypeName,
  usesDefaultHistoricalIrrigationPercentage,
} from "../../api-toolkit/useArcPlc";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { formatPercentage } from "../../utility/formatters";
import { BUDGET_STATUS_ACTIVE } from "../Budget/helpers";
import { TilleyArcPlcCard } from "../Cards/TilleyArcPlcCard";
import InsuranceStressTestParametersDialog from "../Insurance/components/InsuranceStressTestParametersDialog";
import {
  ArcPlcService,
  MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED,
  PROGRAM_NAME_ARCCO,
  PROGRAM_NAME_PLC,
} from "./ArcPlcService";
import { ArcCoStressTestGrid } from "./components/ArcCoStressTestGrid";
import { ArcCoWorksheet } from "./components/ArcCoWorksheet";
import ArcPlcProducerFarmCropCard from "./components/ArcPlcProducerFarmCropCard";
import { PlcStressTestGrid } from "./components/PlcStressTestGrid";
import { PlcWorksheet } from "./components/PlcWorksheet";

// Display mode constants
const DISPLAY_MODE_PLANTED_ACRE = "planted_acre";
const DISPLAY_MODE_BASE_ACRE = "base_acre";

/** Groups each Based On value + label on small screens for scanability (desktop unchanged below `md`). */
const basedOnMetricGroupSx = (theme) => ({
  width: "100%",
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    p: 1.25,
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: "background.paper",
    boxShadow:
      theme.palette.mode === "light" ? "0 1px 2px rgba(15, 23, 42, 0.06)" : "0 1px 3px rgba(0, 0, 0, 0.45)",
  },
});

const basedOnOutlinedFieldSx = (theme) => ({
  [theme.breakpoints.down("md")]: {
    "& .MuiOutlinedInput-root": {
      bgcolor: theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.grey[900],
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.action.active,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.text.secondary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },
});

const basedOnLabelSx = (theme) => ({
  wordBreak: "break-word",
  [theme.breakpoints.down("md")]: {
    fontWeight: 600,
    fontSize: "0.75rem",
    lineHeight: 1.35,
    letterSpacing: "0.02em",
    color: "text.secondary",
    mt: 0.25,
  },
});

const basedOnReadOnlyValueSx = (theme) => ({
  [theme.breakpoints.down("md")]: {
    fontSize: "1.35rem",
    fontWeight: 700,
    lineHeight: 1.25,
  },
});

export default function ArcPlcCountyBaseAcreWorkspace() {
  const { globalState } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();
  const { crop_year, location_state_name, location_county_name, commodity_name, type_name } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();
  const [isLoadingBaseAcres, setIsLoadingBaseAcres] = useState(true);
  const [isLoadingParameters, setIsLoadingParameters] = useState(true);

  // --- --- ---

  const [baseAcresWorkspaceMode] = useState(WORKSPACE_VIEW_CARD);
  const { state: baseAcres, actions: baseAcreActions } = useCardController([]);

  // Display mode state (shared by both ARC-CO and PLC)
  const [arcCoDisplayMode, setArcCoDisplayMode] = useState(DISPLAY_MODE_PLANTED_ACRE);

  // Budget County Practices state (needed for stress tests)
  const [budgetCountyPractices, setBudgetCountyPractices] = useState([]);

  const budgetTotalReportedAcreage = useMemo(() => {
    return budgetCountyPractices?.length > 0
      ? budgetCountyPractices.reduce((accumulator, currentValue) => accumulator + currentValue?.reported_acreage, 0)
      : 0;
  }, [budgetCountyPractices]);

  const historicalIrrigationPercentage = useMemo(() => {
    if (!baseAcres?.length) return null;
    return getShareWeightedHistoricalIrrigationPercentage(baseAcres);
  }, [baseAcres]);

  const [userStressTestParameters, setUserStressTestParameters] = useState(null);
  const [isStressTestOptionsDialogOpen, setIsStressTestOptionsDialogOpen] = useState(false);

  const [arcCoCountyYieldOverride, setArcCoCountyYieldOverride] = useState(null);
  const [isLoadingArcCoCountyYieldOverride, setIsLoadingArcCoCountyYieldOverride] = useState(true);

  const {
    isLoadingYieldForecast,
    isLoadingCountyData,
    isLoadingHistoricalYieldTrend,
    yieldForecastValues,
    rmaProjectedYieldIrrigated,
    rmaProjectedYieldNonIrrigated,
    rmaHistoricalYieldIrrigated,
    rmaHistoricalYieldNonIrrigated,
  } = useWeatherReport({
    dateRange: { start: new Date(crop_year, 0, 1), end: new Date(crop_year, 11, 31) },
    locationState: { state_name: location_state_name, county_name: location_county_name },
    currentYear: crop_year,
    selectedCropType: commodity_name,
    globalState,
    enqueueSnackbar,
  });

  const [prices, setPrices] = useState({});
  const loadPriceData = async () => {
    try {
      if (location_state_name && location_county_name && commodity_name) {
        const pricesParameters = {
          reinsurance_year: String(crop_year),
          state_name: location_state_name,
          county_name: location_county_name,
          commodity_name: commodity_name,
        };

        if (type_name) pricesParameters.type_name = type_name;

        const _prices = await RmaApiHelper.selectPrices(pricesParameters);
        if (_prices?.length) {
          setPrices(_prices[0]);
        }
      }
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    loadPriceData();
  }, [location_state_name, location_county_name, commodity_name, type_name]);

  const { arcCoCountyYieldOverrides, reload: reloadArcCoCountyYieldOverrides } = useArcCoCountyYieldOverrides({
    crop_year,
  });

  // Initialize useArcPlc with all stress test dependencies
  const arcPlc = useArcPlc({
    baseAcres,
    budgetCountyPractices,
    budgetTotalReportedAcreage,
    historicalIrrigationPercentage,
    arcCoDisplayMode,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name: normalizeArcPlcRouteTypeName(type_name),
    crop_year,
    arcCoCountyYieldOverrides,
    countyYieldOverrideRecord: arcCoCountyYieldOverride,
    // User's saved params override hook defaults; null lets the hook use its own defaults
    arcCoStressTestParametersIrrigated: userStressTestParameters,
    arcCoStressTestParametersNonIrrigated: userStressTestParameters,
    plcStressTestParameters: userStressTestParameters,
    // Benchmark yields override
    final_county_yield_irrigated: arcCoCountyYieldOverride?.final_county_yield_irrigated,
    final_county_yield_non_irrigated: arcCoCountyYieldOverride?.final_county_yield_non_irrigated,
    market_year_average_price: arcCoCountyYieldOverride?.market_year_average_price,
  });

  const isHipCounty = useMemo(() => {
    const byPractice = arcPlc?.totals?.arccoResponsesByPractice;
    if (!byPractice?.length) return false;
    const hasIrrigated = byPractice.some((row) => row?.practice_name === PRACTICE_NAME_IRRIGATED);
    const hasNonIrrigated = byPractice.some((row) => row?.practice_name === PRACTICE_NAME_NON_IRRIGATED);
    return hasIrrigated && hasNonIrrigated;
  }, [arcPlc?.totals?.arccoResponsesByPractice]);

  const showDefaultHipWarning = isHipCounty && usesDefaultHistoricalIrrigationPercentage(baseAcres);

  // Effective params: user's saved selection, or the hook's computed defaults
  const initialStressTestParameters = userStressTestParameters ?? arcPlc.stressTestParameters;

  // Extract stress test results from hook
  const {
    arcCo: arcCoStressTestResults,
    arcCoIrrigated: arcCoStressTestResultsIrrigated,
    arcCoNonIrrigated: arcCoStressTestResultsNonIrrigated,
    plc: plcStressTestResults,
  } = arcPlc.stressTestResults || {};

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBaseAcres = async () => {
    try {
      setIsLoadingBaseAcres(false);

      const baseAcresFilter = {
        crop_year,
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        commodity_name,
        ...(type_name && { type_name }),
        is_active: true,
      };
      const baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      baseAcreActions.replace(baseAcres);

      setIsLoadingBaseAcres(false);
    } catch (err) {
      setIsLoadingBaseAcres(false);
      baseAcreActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (location_state_name && location_county_name && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [location_state_name, location_county_name, commodity_name, type_name, globalState?.producer_token]);

  // ---

  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const isSavingArcCoCountyYieldOverrideRef = useRef(false);

  const handleChangeArcCoCountyYieldOverride = async (event) => {
    if (isSavingArcCoCountyYieldOverrideRef.current) return;

    isSavingArcCoCountyYieldOverrideRef.current = true;

    try {
      const fieldName = event.target.name;
      const value = isClearedFieldValue(event.target.value) ? null : event.target.value;

      if (arcCoCountyYieldOverride?.[fieldName] === value) {
        return;
      }

      if (value !== null && Number(value) < 0) {
        formStatusActions.setIsFormDirty(true, false);
        return;
      }

      formStatusActions.setIsFormDirty(true, true);

      const id = arcCoCountyYieldOverride?.id || uuidv4();
      const typeName = normalizeArcPlcRouteTypeName(type_name);

      const payload = {
        ...arcCoCountyYieldOverride,
        [fieldName]: value,
        id,
        producer_token: globalState.producer_token,
        state_name: location_state_name,
        county_name: location_county_name,
        commodity_name,
        type_name: typeName,
        crop_year,
        is_active: true,
      };

      // Apply immediately so worksheets and payment cards recalculate before the API returns.
      setArcCoCountyYieldOverride(payload);

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      await api.callPost("ArcCoCountyYieldOverride", payload, globalState);

      await reloadArcCoCountyYieldOverrides();

      formStatusActions.setIsSaving(false);
    } catch (err) {
      formStatusActions.setIsFormDirty(true, false);
      handleError(err);
      await loadArcCoCountyYieldOverride();
    } finally {
      isSavingArcCoCountyYieldOverrideRef.current = false;
    }
  };

  const handleClearArcCoCountyYieldOverrideField = async (fieldName) => {
    await handleChangeArcCoCountyYieldOverride({
      target: {
        name: fieldName,
        value: null,
      },
    });
  };

  const getClearOverrideAdornment = (fieldName) => {
    const hasOverride = !isClearedFieldValue(arcCoCountyYieldOverride?.[fieldName]);
    if (!hasOverride) return null;

    return (
      <ClearFieldAdornment
        label={`clear ${fieldName} override`}
        onClear={() => handleClearArcCoCountyYieldOverrideField(fieldName)}
        disabled={isLoadingArcCoCountyYieldOverride || arcPlc?.isLoading}
      />
    );
  };

  // ---

  async function loadArcCoCountyYieldOverride() {
    setIsLoadingArcCoCountyYieldOverride(true);

    try {
      const typeName = normalizeArcPlcRouteTypeName(type_name);

      const arcCoCountyYieldOverrideFilter = {
        producer_token: globalState.producer_token,
        state_name: location_state_name,
        county_name: location_county_name,
        commodity_name,
        type_name: typeName,
        crop_year,
        is_active: true,
      };

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      const overrides = await api.callPost(
        "ArcCoCountyYieldOverride/search",
        arcCoCountyYieldOverrideFilter,
        globalState
      );

      if (overrides?.length) {
        setArcCoCountyYieldOverride(overrides[0]);
      } else {
        setArcCoCountyYieldOverride(null);
      }
    } catch (err) {
      setArcCoCountyYieldOverride(null);
      handleError(err);
    } finally {
      setIsLoadingArcCoCountyYieldOverride(false);
    }
  };

  useEffect(() => {
    if (location_state_name && location_county_name && commodity_name && crop_year && globalState?.producer_token) {
      loadArcCoCountyYieldOverride();
    }
  }, [location_state_name, location_county_name, commodity_name, type_name, crop_year, globalState?.producer_token]);

  // --- --- ---

  const [programElection, setProgramElection] = useState(null);
  const loadProgramElection = async () => {
    try {
      const programElectionFilter = {
        crop_year,
        state_name: location_state_name,
        county_name: location_county_name,
        commodity_name,
        program_category: MODULE_ARC_PLC,
        is_active: true,
      };
      const _programElections = await UsersApiHelper.users_selectObject(
        "ProgramElection",
        programElectionFilter,
        globalState
      );
      if (_programElections?.length) {
        setProgramElection(_programElections[0]);
      } else {
        setProgramElection(null);
      }
    } catch (err) {
      setProgramElection(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (crop_year && location_state_name && location_county_name && commodity_name && globalState?.producer_token) {
      loadProgramElection();
    } else {
      setProgramElection(null);
    }
  }, [crop_year, location_state_name, location_county_name, commodity_name, type_name, globalState?.producer_token]);

  // ---

  const [isLoadingARCCOPrices, setIsLoadingARCCOPrices] = useState(true);

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      setIsLoadingARCCOPrices(false);
    }
  }, [baseAcres, globalState?.producer_token]);

  const [isLoadingPLCPaymentRates, setIsLoadingPLCPaymentRates] = useState(true);
  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      setIsLoadingPLCPaymentRates(false);
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [marketYearAveragePrices, setMarketYearAveragePrices] = useState(null);

  const loadMarketYearAveragePrices = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _marketYearAveragePrices = await arcPlcActions.getMarketYearAveragePrices();
      if (_marketYearAveragePrices?.length && _marketYearAveragePrices[0]?.MarketYearAveragePrices?.length) {
        setMarketYearAveragePrices(_marketYearAveragePrices[0]?.MarketYearAveragePrices[0]);
      }
    } catch (err) {
      setMarketYearAveragePrices(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadMarketYearAveragePrices();
    }
  }, [baseAcres, globalState?.producer_token]);

  const defaultStressTestParameters = useMemo(() => {
    const benchmarkYield = arcPlc?.benchmark_yield_non_irrigated ?? arcPlc?.benchmark_yield_irrigated;
    if (benchmarkYield == null || marketYearAveragePrices?.price == null) return null;
    if (commodity_name === COMMODITY_NAME_COTTON && arcPlc?.totals?.plc_yield == null) return null;
    const { actions } = new ArcPlcService({ baseAcres });
    return actions.createStressTestParameters({
      commodity_name,
      benchmarkYield,
      marketYearAveragePrices,
      plcYield: arcPlc?.totals?.plc_yield,
    });
  }, [
    baseAcres,
    commodity_name,
    arcPlc?.benchmark_yield_non_irrigated,
    arcPlc?.benchmark_yield_irrigated,
    arcPlc?.totals?.plc_yield,
    marketYearAveragePrices,
  ]);

  // ---

  const [EffectiveReferencePrices, setEffectiveReferencePrices] = useState(null);

  const loadEffectiveReferencePrices = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _EffectiveReferencePrices = await arcPlcActions.getEffectiveReferencePrices();
      if (_EffectiveReferencePrices?.length) {
        setEffectiveReferencePrices(_EffectiveReferencePrices[0]?.EffectiveReferencePrices);
      }
    } catch (err) {
      setEffectiveReferencePrices(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadEffectiveReferencePrices();
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [ARCCOYields, setARCCOYields] = useState(null);

  const loadARCCOYields = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _arccoYields = await arcPlcActions.getARCCOYields();
      if (_arccoYields?.length) {
        setARCCOYields(_arccoYields);
      }
    } catch (err) {
      setARCCOYields(null);
      handleError(err);
    }
  };

  const ARCCOYieldsIrrigated = useMemo(() => {
    return ARCCOYields?.find((yieldRecord) => yieldRecord?.practice_name === PRACTICE_NAME_IRRIGATED) || null;
  }, [ARCCOYields]);

  const ARCCOYieldsNonIrrigated = useMemo(() => {
    return ARCCOYields?.find((yieldRecord) => yieldRecord?.practice_name === PRACTICE_NAME_NON_IRRIGATED) || null;
  }, [ARCCOYields]);

  const NonIrrigatedScaleFactor = useMemo(() => {
    if (ARCCOYieldsIrrigated && ARCCOYieldsNonIrrigated && ARCCOYieldsIrrigated?.benchmark_yield) {
      return ARCCOYieldsNonIrrigated?.benchmark_yield / ARCCOYieldsIrrigated?.benchmark_yield;
    }
    return null;
  }, [ARCCOYieldsIrrigated, ARCCOYieldsNonIrrigated]);

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadARCCOYields();
    }
  }, [baseAcres, globalState?.producer_token]);

  // --- --- ---

  const handleProgramElectionClick = async (_new_program_name) => {
    const PROGRAM_ELECTION_DEFAULT = {
      crop_year,
      state_name: location_state_name,
      county_name: location_county_name,
      commodity_name,
      program_category: MODULE_ARC_PLC,
      is_active: true,
      producer_token: globalState?.producer_token,
    };

    if (programElection?.program_election === _new_program_name) programElection.is_active = !programElection.is_active;

    const _newProgramElection = programElection || PROGRAM_ELECTION_DEFAULT;
    _newProgramElection.program_election = _new_program_name;

    if (!_newProgramElection.id) {
      _newProgramElection.id = uuidv4();
    }

    UsersApiHelper.updateProgramElection(_newProgramElection, globalState)
      .then(() => {
        loadProgramElection();
      })
      .catch((err) => {
        handleError(err);
      });
  };

  // Handle display mode menu click
  const handleArcCoDisplayModeClick = async (_displayMode, popupState) => {
    setArcCoDisplayMode(_displayMode);
    popupState?.close();
  };

  // Get display label for selected mode
  const getArcCoDisplayModeLabel = (mode) => {
    switch (mode) {
      case DISPLAY_MODE_PLANTED_ACRE:
        return "By Planted Acre";
      case DISPLAY_MODE_BASE_ACRE:
        return "By Base Acre";
      default:
        return "By Planted Acre";
    }
  };

  // Get display label for PLC mode
  const getPlcDisplayModeLabel = (mode) => {
    switch (mode) {
      case DISPLAY_MODE_PLANTED_ACRE:
        return "per Planted Acre";
      case DISPLAY_MODE_BASE_ACRE:
        return "per Base Acre";
      default:
        return "per Planted Acre";
    }
  };

  // Stress test options handlers
  const handleStressTestOptionsDialogOpenClick = () => {
    setIsStressTestOptionsDialogOpen(true);
  };

  const handleStressTestParametersChange = async (_stressTestParameters) => {
    const paramsWithId = _stressTestParameters?.id
      ? { ..._stressTestParameters }
      : { ..._stressTestParameters, id: uuidv4(), is_active: true };

    setUserStressTestParameters(paramsWithId);

    const apiParams = {
      id: paramsWithId.id,
      yield_low: paramsWithId.yield_low,
      yield_high: paramsWithId.yield_high,
      price_low: paramsWithId.price_low,
      price_high: paramsWithId.price_high,
      county_yield_low: paramsWithId.county_yield_low,
      county_yield_high: paramsWithId.county_yield_high,

      state_name: location_state_name,
      county_name: location_county_name,
      commodity_name,
      program_category: "arc_plc",
      is_active: true,
    };

    UsersApiHelper.updateInsuranceStressTestParameters(apiParams, globalState);
  };

  // --- --- ---

  const calculateArcPlcResponseRecord = (producer_farm_base_acres_id) => {
    if (arcPlc?.responses?.length) {
      const _response = arcPlc.responses.find(
        (_arcplc) => _arcplc.baseAcres?.id === producer_farm_base_acres_id
      )?.arcPlcResponse;
      return _response;
    }
  };

  const loadStressTestParameters = useCallback(async () => {
    if (!globalState?.producer_token) return;

    try {
      const filter = {
        producer_token: globalState.producer_token,
        state_name: location_state_name,
        county_name: location_county_name,
        commodity_name,
        program_category: "arc_plc",
        is_active: true,
      };
      const _stressTestParameters = await UsersApiHelper.users_selectObject(
        "InsuranceStressTestParameters",
        filter,
        globalState
      );
      if (_stressTestParameters?.length) {
        setUserStressTestParameters(_stressTestParameters[0]);
        return _stressTestParameters[0];
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoadingParameters(false);
    }
  }, [globalState?.producer_token, location_state_name, location_county_name, commodity_name, type_name]);

  useEffect(() => {
    if (globalState?.producer_token && location_state_name && location_county_name && commodity_name) {
      loadStressTestParameters();
    } else {
      setIsLoadingParameters(false);
    }
  }, [globalState?.producer_token, location_state_name, location_county_name, commodity_name, type_name]);

  // Budget County Practices loading (moved state to top for hook dependency)
  const loadBudgetCountyPractices = async () => {
    const _budgetFilter = {
      state_name: location_state_name,
      county_name: location_county_name,
      commodity_name: commodity_name,
      year: crop_year,
      status: BUDGET_STATUS_ACTIVE,
      is_active: true,
    };

    try {
      const _budgetCountyPractices = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);
      setBudgetCountyPractices(_budgetCountyPractices);
    } catch (err) {
      setBudgetCountyPractices([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (location_state_name && location_county_name && commodity_name && crop_year && globalState?.producer_token) {
      loadBudgetCountyPractices();
    }
  }, [location_state_name, location_county_name, commodity_name, type_name, crop_year, globalState?.producer_token]);

  const arcPlcTotalBaseAcreage = useMemo(() => {
    return baseAcres?.length > 0
      ? baseAcres.reduce((accumulator, currentValue) => accumulator + currentValue.base_acreage, 0)
      : 0;
  }, [baseAcres]);

  const isStressTestCalculatable = useMemo(() => {
    if (arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE) {
      return budgetTotalReportedAcreage > 0 || budgetCountyPractices?.length > 0;
    }
    if (arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE) {
      return arcPlc?.totals?.base_acreage > 0;
    }
    return false;
  }, [arcCoDisplayMode, budgetTotalReportedAcreage, budgetCountyPractices, arcPlc?.totals?.base_acreage]);

  // Set default display mode based on business rules
  useEffect(() => {
    if (budgetTotalReportedAcreage === 0 || budgetCountyPractices?.length === 0) {
      // Always force to base acre when planted acres is 0 or no budget exists (business rule)
      setArcCoDisplayMode(DISPLAY_MODE_BASE_ACRE);
    }
    // Note: We don't auto-reset to planted acre to preserve user choice
  }, [budgetTotalReportedAcreage, budgetCountyPractices?.length]);

  // --- --- ---

  // Stress test functionality moved to useArcPlc hook

  // --- --- ---

  // PLC stress test functionality moved to useArcPlc hook

  useEffect(() => {
    addAttachments([
      {
        name: `${!commodity_name ? "ARC/PLC" : commodity_name}`,
        content: arcPlc,
      },
    ]);
  }, [arcPlc?.totals?.arcPayment, arcPlc?.totals?.plcPayment, commodity_name]);

  // Ensure we don't show results until the hook has caught up with the loaded parameters
  const isStressTestResultsConsistent = useMemo(() => {
    if (isLoadingParameters) return false;
    if (userStressTestParameters?.id) {
      return arcPlc.stressTestParameters?.id === userStressTestParameters.id;
    }
    return true;
  }, [isLoadingParameters, userStressTestParameters, arcPlc.stressTestParameters]);

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title={
          !commodity_name
            ? "ARC/PLC"
            : commodity_name + (baseAcres?.[0]?.type_name ? " - " + baseAcres?.[0]?.type_name : "")
        }
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
        breadCrumbs={[
          { title: "ARC/PLC " + crop_year, path: "/arcplc" },
          {
            title: location_county_name + ", " + location_state_name,
            path: `/arcplc/${crop_year}/${location_state_name}/${location_county_name}`,
          },
        ]}
      />
      {showDefaultHipWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="default-hip-warning">
          HIP percentage is required for this crop and county. Enter the exact HIP percentage (even if it's 0%) from
          your FSA-156 into your Farm Vault. If HIP is missing from your FSA-156, contact your local FSA office. If no
          HIP percentage is entered, Tilley will calculate using a default of 70% irrigated / 30% non-irrigated.
        </Alert>
      )}
      {/* ARC-CO Calculation Worksheet */}
      {(arcPlc?.totals?.arccoResponsesByPractice?.length || arcPlc?.responses?.length) && (
        <Box sx={{ mb: 4 }}>
          <ArcCoWorksheet
            arcCoCalculations={
              arcPlc.totals.arccoResponsesByPractice?.length
                ? // IRR + NI
                  arcPlc.totals.arccoResponsesByPractice
                : // Single practice
                  arcPlc.responses[0]?.arcPlcResponse?.arcco
            }
            combinedCalculation={calculateCombinedArcCoResults(arcPlc)}
            displayMode={arcCoDisplayMode}
            marketYearAveragePriceStatus={marketYearAveragePrices?.price_status}
          />
        </Box>
      )}
      {/* PLC Calculation Worksheet */}
      {(arcPlc?.totals?.arccoResponsesByPractice?.length || arcPlc?.responses?.length) && (
        <Box sx={{ mb: 4 }}>
          <PlcWorksheet
            plcCalculation={arcPlc.totals.plcCalculation}
            marketYearAveragePriceStatus={marketYearAveragePrices?.price_status}
          />
        </Box>
      )}

      <Typography variant="h1">Program Comparison by County / Crop</Typography>
      <Grid container spacing={2} className="county-base-acres">
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content participating"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!arcPlc?.isLoading && (
                      <>
                        {arcPlc?.totals?.arcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.arcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No ARC-CO payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.arcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.arcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton data-testid="arc-co-payment-skeleton" variant="text" width={150} height={55} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1">
                  ARC-CO
                  <br />
                  PAYMENT
                </Typography>

                <Box display="flex" justifyContent="center" sx={{ marginTop: "15px" }}>
                  <Button
                    size="small"
                    variant={programElection?.program_election === PROGRAM_NAME_ARCCO ? "contained" : "outlined"}
                    color={programElection?.program_election === PROGRAM_NAME_ARCCO ? "primary" : "inherit"}
                    sx={{
                      fontSize: "18px",
                      width: "100%",
                      padding: "8px 24px",
                      background:
                        programElection?.program_election === PROGRAM_NAME_ARCCO ? "primary.main" : "text.secondary",
                      color: programElection?.program_election === PROGRAM_NAME_ARCCO ? undefined : "text.secondary",
                    }}
                    onClick={() => handleProgramElectionClick(PROGRAM_NAME_ARCCO)}
                  >
                    {programElection?.program_election === PROGRAM_NAME_ARCCO ? "Participating" : "Not Participating"}
                  </Button>
                </Box>
              </>
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content participating"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!arcPlc?.isLoading && (
                      <>
                        {arcPlc?.totals?.plcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.plcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No PLC payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.plcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.plcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton data-testid="plc-payment-skeleton" variant="text" width={150} height={55} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1">
                  PLC
                  <br />
                  PAYMENT
                </Typography>

                <Box display="flex" justifyContent="center" sx={{ marginTop: "15px" }}>
                  <Button
                    size="small"
                    variant={programElection?.program_election === PROGRAM_NAME_PLC ? "contained" : "outlined"}
                    color={programElection?.program_election === PROGRAM_NAME_PLC ? "primary" : "inherit"}
                    sx={{
                      fontSize: "18px",
                      width: "100%",
                      padding: "8px 24px",
                      background: programElection?.program_election === PROGRAM_NAME_PLC ? "primary" : "text.secondary",
                      color: programElection?.program_election === PROGRAM_NAME_PLC ? undefined : "text.secondary",
                    }}
                    onClick={() => handleProgramElectionClick(PROGRAM_NAME_PLC)}
                  >
                    {programElection?.program_election === PROGRAM_NAME_PLC ? "Participating" : "Not Participating"}
                  </Button>
                </Box>
              </>
            }
          />
        </Grid>
      </Grid>
      <Divider />
      <Stack direction="row" alignItems="flex-start">
        <Typography variant="h1">Based On</Typography>
        {formStatusComponent}
      </Stack>
      <Stack spacing={1}>
        <Grid
          container
          alignItems="stretch"
          columnSpacing={{ xs: 1, md: 0 }}
          rowSpacing={{ xs: 1.5, md: 0 }}
        >
          <Grid item xs={6} md="auto" sx={{ minWidth: 0, maxWidth: { md: 200 }, display: "flex" }}>
            <Box sx={basedOnMetricGroupSx}>
              <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                <StatefulControlledTextField
                  name="market_year_average_price"
                  isRequired={false}
                  defaultValue={arcCoCountyYieldOverride?.market_year_average_price ?? marketYearAveragePrices?.price}
                  restoreValue={marketYearAveragePrices?.price}
                  variant="outlined"
                  onChange={handleChangeArcCoCountyYieldOverride}
                  maskType={MASK_NUMERIC}
                  isLoading={isLoadingArcCoCountyYieldOverride || arcPlc?.isLoading}
                  allowNull={true}
                  fullWidth
                  sx={basedOnOutlinedFieldSx}
                  endAdornment={getClearOverrideAdornment("market_year_average_price")}
                />
                <Typography variant="body1" sx={basedOnLabelSx}>
                  MYA
                  {marketYearAveragePrices?.price_status === MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED && (
                    <>
                      <br />
                      (PROJECTED)
                    </>
                  )}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch", alignSelf: "stretch" }} md="auto">
            <Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />
          </Grid>

          <Grid item xs={6} md="auto" sx={{ minWidth: 0, display: "flex" }}>
            <Box sx={basedOnMetricGroupSx}>
              <Stack direction="column" spacing={0.5}>
                <SkeletonWrapper variant="text" isLoading={arcPlc?.isLoading}>
                  <Typography variant="h1" sx={basedOnReadOnlyValueSx}>
                    {formatNumber(arcPlc?.totals?.base_acreage, 0)}
                  </Typography>
                </SkeletonWrapper>
                <Typography variant="body1" sx={basedOnLabelSx}>
                  BASE ACRES
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch", alignSelf: "stretch" }} md="auto">
            <Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />
          </Grid>

          <Grid item xs={6} md="auto" sx={{ minWidth: 0, display: "flex" }}>
            <Box sx={basedOnMetricGroupSx}>
              <Stack direction="column" spacing={0.5}>
                <SkeletonWrapper variant="text" isLoading={arcPlc?.isLoading}>
                  <Typography variant="h1" sx={basedOnReadOnlyValueSx}>
                    {formatNumber(budgetTotalReportedAcreage, 0)}
                  </Typography>
                </SkeletonWrapper>
                <Typography variant="body1" sx={basedOnLabelSx}>
                  PLANTED ACRES
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch", alignSelf: "stretch" }} md="auto">
            <Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />
          </Grid>

          {arcPlc?.totals?.arccoResponsesByPractice?.length > 1 ? (
            <>
              <Grid item xs={6} md="auto" sx={{ minWidth: 0, maxWidth: { md: 220 }, display: "flex" }}>
                <Box sx={basedOnMetricGroupSx}>
                  <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                    <StatefulControlledTextField
                      name="final_county_yield_irrigated"
                      isRequired={false}
                      defaultValue={
                        arcCoCountyYieldOverride?.final_county_yield_irrigated ?? arcPlc.benchmark_yield_irrigated
                      }
                      restoreValue={arcPlc.benchmark_yield_irrigated}
                      variant="outlined"
                      onChange={handleChangeArcCoCountyYieldOverride}
                      maskType={MASK_NUMERIC}
                      isLoading={isLoadingArcCoCountyYieldOverride || arcPlc?.isLoading}
                      allowNull={true}
                      fullWidth
                      sx={basedOnOutlinedFieldSx}
                      endAdornment={getClearOverrideAdornment("final_county_yield_irrigated")}
                    />
                    <Typography variant="body1" sx={basedOnLabelSx}>
                      FINAL COUNTY YIELD <br /> IRRIGATED (PROJECTED)
                    </Typography>
                  </Stack>
                </Box>
              </Grid>

              <Grid item sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch", alignSelf: "stretch" }} md="auto">
                <Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />
              </Grid>

              <Grid item xs={12} md="auto" sx={{ minWidth: 0, maxWidth: { md: 220 }, display: "flex" }}>
                <Box sx={basedOnMetricGroupSx}>
                  <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                    <StatefulControlledTextField
                      name="final_county_yield_non_irrigated"
                      isRequired={false}
                      defaultValue={
                        arcCoCountyYieldOverride?.final_county_yield_non_irrigated ?? arcPlc.benchmark_yield_non_irrigated
                      }
                      restoreValue={arcPlc.benchmark_yield_non_irrigated}
                      variant="outlined"
                      onChange={handleChangeArcCoCountyYieldOverride}
                      maskType={MASK_NUMERIC}
                      isLoading={isLoadingArcCoCountyYieldOverride || arcPlc?.isLoading}
                      allowNull={true}
                      fullWidth
                      sx={basedOnOutlinedFieldSx}
                      endAdornment={getClearOverrideAdornment("final_county_yield_non_irrigated")}
                    />
                    <Typography variant="body1" sx={basedOnLabelSx}>
                      FINAL COUNTY YIELD <br /> NON-IRRIGATED (PROJECTED)
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            </>
          ) : (
            <Grid item xs={6} md="auto" sx={{ minWidth: 0, maxWidth: { md: 220 }, display: "flex" }}>
              <Box sx={basedOnMetricGroupSx}>
                <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                  <StatefulControlledTextField
                    name="final_county_yield_non_irrigated"
                    isRequired={false}
                    defaultValue={
                      arcCoCountyYieldOverride?.final_county_yield_non_irrigated ?? arcPlc.benchmark_yield_non_irrigated
                    }
                    restoreValue={arcPlc.benchmark_yield_non_irrigated}
                    variant="outlined"
                    onChange={handleChangeArcCoCountyYieldOverride}
                    maskType={MASK_NUMERIC}
                    isLoading={isLoadingArcCoCountyYieldOverride || arcPlc?.isLoading}
                    allowNull={true}
                    fullWidth
                    sx={basedOnOutlinedFieldSx}
                    endAdornment={getClearOverrideAdornment("final_county_yield_non_irrigated")}
                  />
                  <Typography variant="body1" sx={basedOnLabelSx}>
                    FINAL COUNTY YIELD <br /> (PROJECTED)
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          )}
        </Grid>
        <Card>
          <Box className="weather-dashboard section-container" sx={{ px: "18px", py: "16px" }}>
            <YieldForecast
              selectedCropType={commodity_name}
              isLoadingYieldForecast={isLoadingYieldForecast}
              isLoadingCountyData={isLoadingCountyData}
              isLoadingHistoricalYieldTrend={isLoadingHistoricalYieldTrend}
              yieldForecastValues={yieldForecastValues}
              rmaProjectedYieldIrrigated={rmaProjectedYieldIrrigated}
              rmaProjectedYieldNonIrrigated={rmaProjectedYieldNonIrrigated}
              rmaHistoricalYieldIrrigated={rmaHistoricalYieldIrrigated}
              rmaHistoricalYieldNonIrrigated={rmaHistoricalYieldNonIrrigated}
            />
            {/* Third row: Price information */}
            {prices?.projected_price && (
              <Grid container spacing={1} sx={{ borderTop: "1px solid", borderColor: "grey.200" }}>
                <Grid item xs={6} sx={{ textAlign: "center" }}>
                  <Box>
                    <Typography variant="h5" sx={{ color: "text.primary" }}>
                      {formatPrice(prices?.projected_price, 4)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      USDA Insurance Price
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Card>
      </Stack>
      <Divider />
      <Typography variant="h1">PLC</Typography>
      <Stack direction="row" spacing={1}>
        <Stack direction="column">
          <SkeletonWrapper variant="text" isLoading={isLoadingPLCPaymentRates}>
            <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
              {formatPrice(
                EffectiveReferencePrices?.length && EffectiveReferencePrices[0]?.effective_reference_price,
                4
              )}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">ERP PRICE</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <SkeletonWrapper variant="text" isLoading={arcPlc?.isLoading}>
            <Typography variant="h1">{formatNumber(arcPlc?.totals?.plc_yield, 0)}</Typography>
          </SkeletonWrapper>
          <Typography variant="body1">PLC YIELD</Typography>
        </Stack>
      </Stack>
      <Divider />
      {Boolean(arcPlc.totals.arccoResponsesByPractice?.length) &&
        arcPlc.totals.arccoResponsesByPractice?.map((_arccoResponse, index) => (
          <Fragment key={index}>
            <Typography variant="h1">
              ARC-CO
              {Boolean(_arccoResponse?.practice_name) && (
                <>
                  {": " + _arccoResponse?.practice_name}
                  <br />
                </>
              )}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2, mb: 2 }}>
              <Stack direction="column">
                <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                  {formatPrice(_arccoResponse?.benchmark_price, 4)}
                </Typography>
                <Typography variant="body1">
                  BENCHMARK
                  <br />
                  PRICE
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack direction="column">
                <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                  {formatNumber(_arccoResponse?.benchmark_yield, 1)}
                </Typography>
                <Typography variant="body1">
                  BENCHMARK
                  <br />
                  YIELD
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack direction="column">
                <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                  {formatPrice(_arccoResponse?.benchmark_guarantee, 2)}
                </Typography>
                <Typography variant="body1">
                  GUARANTEE
                  <br />/ BASE ACRE
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack direction="column">
                <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                  {formatPrice(_arccoResponse?.arcPayment / arcPlc?.totals?.base_acreage, 2)}
                </Typography>
                <Typography variant="body1">
                  PAYMENT
                  <br />/ BASE ACRE
                  {marketYearAveragePrices?.price_status === MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED && (
                    <> (PROJECTED)</>
                  )}
                </Typography>
              </Stack>

              {isHipCounty && (
                <>
                  <Divider orientation="vertical" flexItem />

                  <Stack direction="column">
                    <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                      {formatPercentage(
                        _arccoResponse?.practice_name === PRACTICE_NAME_IRRIGATED
                          ? _arccoResponse?.historical_irrigation_percentage
                          : 1 - _arccoResponse?.historical_irrigation_percentage,
                        0
                      )}
                    </Typography>
                    <Typography variant="body1">
                      HIP
                      <br />
                      {_arccoResponse?.practice_name === PRACTICE_NAME_IRRIGATED ? "IRR" : "NI"} FACTOR
                    </Typography>
                  </Stack>
                  <Divider orientation="vertical" flexItem />

                  <Stack direction="column">
                    <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                      {formatPrice(
                        (_arccoResponse?.arcPayment *
                          (_arccoResponse?.practice_name === PRACTICE_NAME_IRRIGATED
                            ? _arccoResponse?.historical_irrigation_percentage
                            : 1 - _arccoResponse?.historical_irrigation_percentage)) /
                          arcPlc?.totals?.base_acreage,
                        2
                      )}
                    </Typography>
                    <Typography variant="body1">
                      PAYMENT
                      <br />/ BASE ACRE
                      {marketYearAveragePrices?.price_status === MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED && (
                        <> (PROJECTED)</>
                      )}
                      <br />
                      (HIP ADJUSTED)
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>

            <Divider />
          </Fragment>
        ))}
      <Typography variant="h1" sx={{ textTransform: "capitalize" }}>
        Base Acres
      </Typography>
      {baseAcresWorkspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {baseAcres.map((_currentBaseAcres, index) => {
            const _currentArcPlcResponse = calculateArcPlcResponseRecord(_currentBaseAcres?.id);
            return (
              <ArcPlcProducerFarmCropCard
                key={index}
                currentBaseAcres={_currentBaseAcres}
                currentArcPlc={_currentArcPlcResponse}
                isLoadingArcPlc={arcPlc?.isLoading}
                isErrorArcPlc={arcPlc?.isError}
              />
            );
          })}
          {!baseAcres?.length && (
            <EmptyCard
              text="No base acres listed"
              secondaryText="Get started by importing or adding base acres"
              isLoading={isLoadingBaseAcres}
            />
          )}
        </Box>
      )}
      <Divider />
      <Grid container direction="row" spacing={0}>
        <Grid item sm={12} md={6} sx={{ pr: 2, borderRight: 1, borderColor: "divider" }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <Typography variant="h1">ARC-CO Payments {getArcCoDisplayModeLabel(arcCoDisplayMode)}</Typography>
            <PopupState variant="popover" popupId="arcco-display-mode-popup-menu">
              {(popupState) => (
                <>
                  <Button variant="outlined" {...bindTrigger(popupState)}>
                    {getArcCoDisplayModeLabel(arcCoDisplayMode)} <ArrowDropDownOutlined />
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={() => handleArcCoDisplayModeClick(DISPLAY_MODE_PLANTED_ACRE, popupState)}
                      disabled={budgetTotalReportedAcreage === 0}
                    >
                      {arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      By Planted Acre
                    </MenuItem>
                    <MenuItem onClick={() => handleArcCoDisplayModeClick(DISPLAY_MODE_BASE_ACRE, popupState)}>
                      {arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE ? (
                        <CheckOutlined sx={{ mr: 1 }} />
                      ) : (
                        <Icon sx={{ mr: 1 }} />
                      )}
                      By Base Acre
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>
            <Button
              variant="outlined"
              onClick={handleStressTestOptionsDialogOpenClick}
              disabled={!initialStressTestParameters}
            >
              <CalculateOutlined sx={{ mr: 1 }} /> Options
            </Button>
            {(!arcCoStressTestResults?.results || !isStressTestResultsConsistent) && isStressTestCalculatable && (
              <CircularProgress data-testid="arc-co-stress-test-loading" />
            )}
          </Stack>
          <Box>
            {!isStressTestCalculatable && (
              <>
                <Alert severity="warning">
                  {arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE ? "No Budget found." : "No Base Acres found."}
                  <Divider />
                  {arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE
                    ? "To view stress test results, please add a budget or switch to 'By Base Acre'."
                    : "To view stress test results, please add base acres to your farm."}
                </Alert>
              </>
            )}
            {isStressTestCalculatable && isStressTestResultsConsistent && (
              <ArcCoStressTestGrid
                commodity_name={commodity_name}
                displayFieldName={
                  arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE
                    ? "indemnity_total_per_base_acre"
                    : "indemnity_total_per_acre"
                }
                xAxisFieldName={commodity_name === COMMODITY_NAME_COTTON ? "county_yield_seedcotton" : "county_yield"}
                xAxisDisplayName={
                  commodity_name === COMMODITY_NAME_COTTON
                    ? "County Yield (Seed Cotton)"
                    : "County Yield" + (arcPlc?.totals?.arccoResponsesByPractice?.length > 1 ? " (Irrigated)" : "")
                }
                yAxisFieldName={"price"}
                yAxisDisplayName={"MYA"}
                stressTestResults={arcCoStressTestResults?.results}
                stressTestResultsIrrigated={arcCoStressTestResultsIrrigated?.results}
                stressTestResultsNonIrrigated={arcCoStressTestResultsNonIrrigated?.results}
                nonIrrigatedScaleFactor={NonIrrigatedScaleFactor}
                historicalIrrigationPercentage={historicalIrrigationPercentage}
                doMarkNegativeValues={true}
                maxLossValue={-1}
                displayMode={arcCoDisplayMode}
                budgetTotalReportedAcreage={budgetTotalReportedAcreage || arcPlcTotalBaseAcreage}
                arcPlcTotalBaseAcreage={arcPlc?.totals?.base_acreage}
                highlightRowIndex={null}
              />
            )}
          </Box>
        </Grid>

        <Grid item sm={12} md={6} sx={{ pl: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2, minHeight: "48px" }}>
            <Typography variant="h1">PLC Payments {getPlcDisplayModeLabel(arcCoDisplayMode)}</Typography>
            {(!plcStressTestResults?.results || !isStressTestResultsConsistent) && isStressTestCalculatable && (
              <CircularProgress data-testid="plc-stress-test-loading" />
            )}
          </Stack>
          <Box>
            {!isStressTestCalculatable && (
              <Alert severity="warning">
                {arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE ? "No Budget found." : "No Base Acres found."}
                <Divider />
                {arcCoDisplayMode === DISPLAY_MODE_PLANTED_ACRE
                  ? "To view stress test results, please add a budget or switch to 'By Base Acre'."
                  : "To view stress test results, please add base acres to your farm."}
              </Alert>
            )}
            {isStressTestCalculatable && isStressTestResultsConsistent && (
              <PlcStressTestGrid
                displayFieldName={
                  arcCoDisplayMode === DISPLAY_MODE_BASE_ACRE
                    ? "indemnity_total_per_base_acre"
                    : "indemnity_total_per_acre"
                }
                xAxisValue={arcPlc?.totals?.plc_yield}
                xAxisFieldName={"plc_yield"}
                xAxisDisplayName={"PLC Yield"}
                yAxisFieldName={"price"}
                yAxisDisplayName={"Effective Price"}
                stressTestResults={plcStressTestResults?.results}
                doMarkNegativeValues={true}
                maxLossValue={-1}
                displayMode={arcCoDisplayMode}
                budgetTotalReportedAcreage={budgetTotalReportedAcreage || arcPlcTotalBaseAcreage}
                arcPlcTotalBaseAcreage={arcPlc?.totals?.base_acreage}
                highlightRowIndex={null}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Stress Test Options Dialog */}
      <Dialog open={isStressTestOptionsDialogOpen}>
        <InsuranceStressTestParametersDialog
          onClose={() => setIsStressTestOptionsDialogOpen(false)}
          onChange={handleStressTestParametersChange}
          initialStressTestParameters={initialStressTestParameters}
          budgetCountyPractice={budgetCountyPractices?.[0]}
          countyData={defaultStressTestParameters}
        />
      </Dialog>

      {confirmDialogComponent}
      <WorkspaceActionBar content={<></>} />
    </>
  );
}
